import { ThemeOptions } from "@mui/material"

const frostedBackdrop = {
  backgroundColor: "rgba(255, 255, 255, 0.32)",
  backdropFilter: "saturate(180%) blur(26px)",
  border: "1px solid rgba(255, 255, 255, 0.45)",
  borderRadius: "5px",
  boxShadow: "0 18px 35px -25px rgba(15, 23, 42, 0.45)"
}

const lightTheme: ThemeOptions = {
  palette: {
    mode: "light",
    primary: { main: "#4DA0FF", light: "#8EC5FF", dark: "#2A7BD9" },
    secondary: { main: "#9C6BFF", light: "#BC96FF", dark: "#6A3BDF" },
    background: { default: "#F5F8FF", paper: "rgba(255, 255, 255, 0.72)" },
    text: { primary: "#0F172A", secondary: "rgba(15, 23, 42, 0.65)" },
    divider: "rgba(15, 23, 42, 0.08)"
  },
  typography: {
    fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif",
    h1: { fontWeight: 600, letterSpacing: "-0.02em" },
    h2: { fontWeight: 600, letterSpacing: "-0.02em" },
    h3: { fontWeight: 600, letterSpacing: "-0.015em" },
    h4: { fontWeight: 600, letterSpacing: "-0.01em" },
    h5: { fontWeight: 600, letterSpacing: "-0.005em" },
    h6: { fontWeight: 600, letterSpacing: "0em" },
    subtitle1: { fontWeight: 500, letterSpacing: "0.01em" },
    button: { textTransform: "none", fontWeight: 600, letterSpacing: "0.01em" }
  },
  shape: { borderRadius: 5 },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background:
            "radial-gradient(circle at 20% 20%, rgba(154, 211, 255, 0.45) 0%, rgba(245, 247, 255, 0.35) 20%, rgba(237, 238, 255, 0.55) 45%, rgba(244, 245, 255, 0.3) 60%, rgba(255, 255, 255, 0.4) 100%)",
          minHeight: "100vh",
          color: "#0F172A"
        },
        "::-webkit-scrollbar": { width: "10px", height: "10px" },
        "::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(104, 113, 136, 0.35)",
          borderRadius: "5px",
          border: "2px solid transparent",
          backgroundClip: "content-box"
        },
        "::-webkit-scrollbar-track": { background: "transparent" }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: { ...frostedBackdrop, backgroundImage: "linear-gradient(135deg, rgba(255, 255, 255, 0.45), rgba(255, 255, 255, 0.25))" }
      }
    },
    MuiCard: { styleOverrides: { root: { ...frostedBackdrop, borderRadius: "5px", padding: "8px" } } },
    MuiAppBar: { styleOverrides: { root: { ...frostedBackdrop, backgroundColor: "rgba(255, 255, 255, 0.6)" } } },
    MuiDrawer: { styleOverrides: { paper: { ...frostedBackdrop, backgroundColor: "rgba(244, 246, 255, 0.72)" } } },
    MuiDialog: {
      styleOverrides: {
        paper: { ...frostedBackdrop, backgroundImage: "linear-gradient(120deg, rgba(255, 255, 255, 0.55), rgba(255, 255, 255, 0.35))" }
      }
    },
    MuiTooltip: { styleOverrides: { tooltip: { ...frostedBackdrop, color: "#0F172A", fontWeight: 500 } } },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 5,
          padding: "6px 16px",
          fontWeight: 600,
          letterSpacing: "0.01em",
          transition: "all 0.25s ease",
          backdropFilter: "saturate(180%) blur(20px)",
          border: "1px solid rgba(255, 255, 255, 0.45)",
          boxShadow: "0 14px 28px -24px rgba(15, 23, 42, 0.55)",
          color: "#0F172A",
          backgroundImage: "linear-gradient(135deg, rgba(255, 255, 255, 0.42), rgba(255, 255, 255, 0.18))",
          "&:hover": {
            boxShadow: "0 18px 36px -24px rgba(15, 23, 42, 0.65)",
            transform: "translateY(-1px)",
            backgroundImage: "linear-gradient(135deg, rgba(255, 255, 255, 0.55), rgba(255, 255, 255, 0.25))"
          },
          "&:active": { transform: "translateY(0)", boxShadow: "0 10px 24px -18px rgba(15, 23, 42, 0.5)" },
          "&.Mui-disabled": {
            backgroundImage: "linear-gradient(135deg, rgba(255, 255, 255, 0.25), rgba(255, 255, 255, 0.12))",
            color: "rgba(15, 23, 42, 0.35)",
            borderColor: "rgba(255, 255, 255, 0.25)",
            boxShadow: "none",
            backdropFilter: "saturate(120%) blur(14px)"
          }
        },
        contained: {
          backgroundImage: "linear-gradient(135deg, rgba(255, 255, 255, 0.45), rgba(230, 245, 255, 0.25))",
          border: "1px solid rgba(255, 255, 255, 0.55)",
          color: "#0F172A",
          "&:hover": { backgroundImage: "linear-gradient(135deg, rgba(255, 255, 255, 0.6), rgba(220, 238, 255, 0.35))" }
        },
        outlined: {
          borderColor: "rgba(77, 160, 255, 0.35)",
          backgroundColor: "rgba(255, 255, 255, 0.12)",
          "&:hover": { borderColor: "rgba(77, 160, 255, 0.55)", backgroundColor: "rgba(255, 255, 255, 0.2)" }
        },
        text: {
          border: "1px solid transparent",
          backgroundColor: "rgba(255, 255, 255, 0.08)",
          "&:hover": { borderColor: "rgba(77, 160, 255, 0.25)", backgroundColor: "rgba(255, 255, 255, 0.16)" }
        }
      }
    },
    MuiTableContainer: { styleOverrides: { root: { ...frostedBackdrop } } }
  }
}

export default lightTheme
